import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useWritable } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatKop, kopToRubString, rubStringToKop } from '../../lib/money';
import { pluralWithCount } from '../../lib/plural';
import { GripIcon } from '../../ui/icons';
import { Notice, Pill } from '../../ui/parts';
import { useSortable } from '../use-sortable';
import { useAdminDirections, useAdminServices, useServiceMutations } from '../api';
import type { Direction, Service } from '../types';

/**
 * Вкладка «Услуги»: что организация продаёт и по какой базовой цене.
 *
 * Список разложен по категориям, а не свален в одну ленту: у организации с
 * пятью категориями плоский список читается как случайный набор, и найти в нём
 * нужную услугу можно только перебором. Порядок внутри категории задаётся
 * перетаскиванием — в нём же услуги увидит клиент при записи.
 */
export function Services() {
  const writable = useWritable();
  const navigate = useNavigate();
  const services = useAdminServices();
  const directions = useAdminDirections();

  useScreen('Услуги', null);

  const list = services.data ?? [];
  const active = list.filter((s) => s.is_active);
  const dirs = directions.data ?? [];

  // Услуги категории, которой уже нет (её удалили в архив), не должны пропадать
  // с экрана: их надо увидеть, чтобы перенести или скрыть.
  const known = new Set(dirs.map((d) => d.id));
  const orphans = list.filter((s) => !known.has(s.direction_id));

  return (
    <Screen>
      {/* Кнопка стоит наверху, а не под списком: услуг у организации десятки,
          и добираться до неё скроллом через все категории приходилось бы каждый раз. */}
      {writable ? (
        <button className="add" onClick={() => navigate('/admin/services/add')}>＋ Добавить услугу</button>
      ) : null}

      {/* Заголовка «Все услуги» здесь больше нет: над каждой группой стоит имя
          категории, и второй заголовок подряд читался бы как шум. */}
      <p className="lead">
        {active.length
          ? pluralWithCount(active.length, 'активная услуга', 'активные услуги', 'активных услуг')
          : 'Активных услуг нет'}
      </p>

      {services.isPending ? <p className="lead">Загружаем…</p> : null}

      {dirs.map((d) => (
        <ServiceGroup
          key={d.id}
          direction={d}
          services={list.filter((s) => s.direction_id === d.id)}
          writable={writable}
        />
      ))}

      {orphans.length ? (
        <ServiceGroup direction={null} services={orphans} writable={false} />
      ) : null}

      {!services.isPending && list.length === 0 ? (
        <p className="lead">Услуг пока нет — добавьте первую.</p>
      ) : null}

      <Notice kind="calm">
        Базовая цена — по умолчанию. У специалиста может быть своя цена и своя выплата.
      </Notice>
    </Screen>
  );
}

/**
 * Одна категория со своими услугами. Отдельный компонент, потому что состояние
 * перетаскивания у каждой группы своё: тащить строку можно только внутри её
 * категории — услуга без категории в схеме не живёт.
 */
function ServiceGroup({ direction, services, writable }: {
  direction: Direction | null;
  services: Service[];
  writable: boolean;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { reorder } = useServiceMutations();

  const byId = new Map(services.map((s) => [s.id, s]));
  const { list, dragId, gripProps, clear } = useSortable(
    services.map((s) => s.id),
    (ids) => {
      if (!direction) return;
      reorder.mutate({ directionId: direction.id, ids }, {
        onSuccess: clear,
        onError: (e) => {
          clear();
          toast(e instanceof ApiError ? e.message : 'Не получилось изменить порядок');
        },
      });
    },
    '.srv',
  );

  const rows = list.map((id) => byId.get(id)).filter(Boolean) as Service[];
  const draggable = writable && rows.length > 1;

  return (
    <div className="srv-group">
      <div className="sechead">
        <p className="eyebrow">{direction ? direction.title : 'Без категории'}</p>
        <span className="sechead-count">
          {rows.length ? pluralWithCount(rows.length, 'услуга', 'услуги', 'услуг') : ''}
        </span>
      </div>

      {rows.length ? (
        <div className="srvs">
          {rows.map((s) => (
            <div className={`srv${dragId === s.id ? ' is-drag' : ''}`} key={s.id}>
              {draggable ? (
                <span
                  className="grip"
                  role="button"
                  tabIndex={-1}
                  aria-label={`Переставить «${s.title}»`}
                  {...gripProps(s.id)}
                >
                  <GripIcon />
                </span>
              ) : null}

              <button className="srv-body" type="button" onClick={() => navigate(`/admin/services/${s.id}`)}>
                <b>{s.title}</b>
                <span>
                  {s.duration_min} мин · {s.is_online ? 'онлайн' : 'очно'}
                  {s.is_intake ? ' · первичная' : ''}
                </span>
              </button>

              {s.is_active ? null : <Pill kind="off">скрыта</Pill>}
              <span className="rw-num">
                <b>{formatKop(s.base_price_kop)}</b>
                <span>{s.rates.length ? `${s.rates.length} спец.` : 'никто не ведёт'}</span>
              </span>
            </div>
          ))}
        </div>
      ) : writable && direction?.is_active ? (
        // Пустая категория должна подсказывать следующий шаг, а не сообщать о
        // пустоте: категорию администратор уже назвал, осталось наполнить.
        // Её id уезжает в подэкран — там она уже выбрана.
        <button className="add" onClick={() => navigate(`/admin/services/add?direction=${direction.id}`)}>
          ＋ Добавить услугу
        </button>
      ) : (
        <p className="lead">Пока пусто</p>
      )}
    </div>
  );
}

/** Карточка услуги: длительность, цена, кто ведёт и на каких условиях. */
export function ServiceCard() {
  const writable = useWritable();
  const navigate = useNavigate();
  const toast = useToast();
  const { id: raw } = useParams();
  const id = Number(raw);
  const services = useAdminServices();
  const directions = useAdminDirections();
  const { update, remove } = useServiceMutations();

  const service = services.data?.find((s) => s.id === id) ?? null;
  const [name, setName] = useState<string | null>(null);
  const [duration, setDuration] = useState<string | null>(null);
  const [price, setPrice] = useState<string | null>(null);
  const [dir, setDir] = useState<number | null>(null);

  useScreen('Карточка услуги', null);

  if (!service) return <Screen><p className="lead">Загружаем услугу…</p></Screen>;

  const dur = duration ?? String(service.duration_min);
  const rub = price ?? kopToRubString(service.base_price_kop);
  const priceKop = rubStringToKop(rub);
  const dirs = directions.data ?? [];
  const dirId = dir ?? service.direction_id;
  const moved = dirId !== service.direction_id;
  const title = name ?? service.title;
  const titleOk = title.trim().length >= 2 && title.trim().length <= 200;
  const margin = service.rates.length
    ? Math.round(service.rates.reduce((a, r) => a + r.margin, 0) / service.rates.length)
    : 0;

  return (
    <Screen>
      {/* Название правится здесь же: заводят услугу быстро, а опечатку в нём
          видит каждый клиент при записи. */}
      <div className={`field${titleOk ? '' : ' is-bad'}`}>
        <label htmlFor="svcTitle">Название</label>
        <input
          id="svcTitle" type="text" value={title} disabled={!writable} aria-invalid={!titleOk}
          onChange={(e) => setName(e.target.value)}
        />
        <span className={titleOk ? 'hint' : 'err'}>
          {titleOk ? 'Так услуга называется для клиента при записи' : 'От 2 до 200 символов'}
        </span>
      </div>

      {service.is_intake ? (
        <p className="lead">Первая запись: с неё начинают новые клиенты</p>
      ) : null}

      {/* Категорию можно поменять: при создании её выбирают вслепую, и ошибиться
          проще всего именно здесь. Переезд применяется вместе с остальной правкой. */}
      <div className="field">
        <label htmlFor="svcDir">Категория</label>
        <select id="svcDir" value={dirId ?? ''} disabled={!writable} onChange={(e) => setDir(Number(e.target.value))}>
          {dirs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          {dirs.some((d) => d.id === dirId) ? null : <option value={dirId ?? ''}>Категория недоступна</option>}
        </select>
        {moved ? <span className="hint">Услуга переедет в «{dirs.find((d) => d.id === dirId)?.title}»</span> : null}
      </div>

      <div className="two">
        <div className="field">
          <label htmlFor="svcDur">Длительность</label>
          <div className="suffix">
            <input id="svcDur" type="number" inputMode="numeric" value={dur} disabled={!writable} onChange={(e) => setDuration(e.target.value)} />
            <i>мин</i>
          </div>
        </div>
        <div className="field">
          <label htmlFor="svcBase">Базовая цена</label>
          <div className="suffix">
            <input id="svcBase" type="number" inputMode="numeric" value={rub} disabled={!writable} onChange={(e) => setPrice(e.target.value)} />
            <i>₽</i>
          </div>
        </div>
      </div>

      <div className="kv">
        <div>
          <span className="k">Формат</span>
          <span className="v">{service.is_online ? 'Онлайн' : 'Очно'}</span>
        </div>
        <div>
          <span className="k">Доступна для записи</span>
          <span className="v">
            <button
              className="switch"
              type="button"
              aria-pressed={!!service.is_active}
              aria-label="Доступна для записи"
              disabled={!writable}
              onClick={() =>
                update.mutate(
                  { id: service.id, is_active: service.is_active ? 0 : 1 },
                  { onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось') },
                )
              }
            />
          </span>
        </div>
      </div>

      <p className="eyebrow" style={{ marginTop: 4 }}>Кто ведёт и на каких условиях</p>
      <div className="rows">
        {service.rates.length ? (
          service.rates.map((r) => (
            <button
              className="rw"
              key={r.specialist_id}
              onClick={() => navigate(`/admin/staff/${r.specialist_id}/services`)}
            >
              <span className="rw-body">
                <b>{r.specialist_name ?? 'Специалист'}</b>
                <span>{r.is_active ? 'ведёт' : 'выключена'}</span>
              </span>
              <span className="rw-num">
                <b>{formatKop(r.price)}</b>
                <span className="pay">{formatKop(r.payout)} специалисту</span>
              </span>
            </button>
          ))
        ) : (
          <p className="lead">Услугу никто не ведёт — в записи она не появится.</p>
        )}
      </div>

      <div className="mline">
        <span>Средняя маржа организации</span>
        <b>{service.rates.length ? formatKop(margin) : '—'}</b>
      </div>

      {writable ? (
        <>
          <button
            className="btn solid"
            disabled={update.isPending || priceKop === null || !titleOk}
            onClick={() =>
              update.mutate(
                {
                  id: service.id, title: title.trim(), duration_min: Number(dur), base_price_kop: priceKop!,
                  direction_id: moved ? dirId : undefined,
                },
                {
                  onSuccess: () => {
                    setDir(null);
                    setName(null);
                    toast(moved ? 'Услуга сохранена и переехала' : 'Услуга сохранена');
                  },
                  // Переезд упирается в ставки специалистов чужой категории:
                  // сервер называет их поимённо, и этот текст надо показать целиком.
                  onError: (e) => toast(
                    e instanceof ApiError ? e.message : 'Не получилось сохранить',
                    { ms: e instanceof ApiError && e.is('SERVICE_HAS_RATES') ? 6000 : undefined },
                  ),
                },
              )
            }
          >
            Сохранить услугу
          </button>
          <button
            className="btn ghost-danger"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(service.id, {
                onSuccess: () => {
                  toast('Услуга архивирована');
                  navigate('/admin/services');
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось архивировать'),
              })
            }
          >
            Архивировать услугу
          </button>
        </>
      ) : null}
    </Screen>
  );
}

/**
 * Подэкран «Новая услуга». Категорию можно передать в ссылке (`?direction=`) —
 * так приходят из пустой группы на экране услуг, где она уже выбрана глазами.
 */
export function ServiceAdd() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const directions = useAdminDirections();
  const { create } = useServiceMutations();

  const list = (directions.data ?? []).filter((d) => d.is_active);
  const preset = list.find((d) => d.id === Number(params.get('direction')))?.id ?? null;
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState('60');
  const [price, setPrice] = useState('3000');
  const [directionId, setDirectionId] = useState<number | null>(null);
  const [online, setOnline] = useState(false);
  const [intake, setIntake] = useState(false);

  const dirId = directionId ?? preset ?? list[0]?.id ?? null;
  const priceKop = rubStringToKop(price);
  const ready = title.trim().length > 2 && Number(duration) >= 5 && priceKop !== null && dirId !== null;

  useScreen('Новая услуга', null);

  return (
    <Screen>
      <div>
        <p className="eyebrow">Новая услуга</p>
        <p className="lead" style={{ marginTop: 6 }}>После сохранения назначьте специалистов и задайте им цены.</p>
      </div>

      <div className="field">
        <label htmlFor="nSvcTitle">Название</label>
        <input id="nSvcTitle" type="text" placeholder="Нейрокоррекция, 60 минут" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <div className="two">
        <div className="field">
          <label htmlFor="nSvcDur">Длительность</label>
          <div className="suffix">
            <input id="nSvcDur" type="number" inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} />
            <i>мин</i>
          </div>
        </div>
        <div className="field">
          <label htmlFor="nSvcPrice">Базовая цена</label>
          <div className="suffix">
            <input id="nSvcPrice" type="number" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} />
            <i>₽</i>
          </div>
        </div>
      </div>

      <div className="two">
        <div className="field">
          <label htmlFor="nSvcDir">Категория</label>
          <select id="nSvcDir" value={dirId ?? ''} onChange={(e) => setDirectionId(Number(e.target.value))}>
            {list.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="nSvcFmt">Формат</label>
          <select id="nSvcFmt" value={online ? '1' : '0'} onChange={(e) => setOnline(e.target.value === '1')}>
            <option value="0">Очно</option>
            <option value="1">Онлайн</option>
          </select>
        </div>
      </div>

      <label className="check">
        <input type="checkbox" checked={intake} onChange={(e) => setIntake(e.target.checked)} />
        <span>Первая запись — с неё записываются новые клиенты, ещё не закреплённые за специалистом</span>
      </label>

      <button
        className="btn solid"
        disabled={!ready || create.isPending}
        onClick={() =>
          create.mutate(
            { directionId: dirId!, title: title.trim(), durationMin: Number(duration), basePriceKop: priceKop!, isOnline: online, isIntake: intake },
            {
              onSuccess: () => {
                toast('Услуга создана');
                navigate('/admin/services');
              },
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось создать'),
            },
          )
        }
      >
        {create.isPending ? 'Создаём…' : 'Создать услугу'}
      </button>
    </Screen>
  );
}
